import React, { useState, useEffect } from "react";
import {
  adminOrderService,
  FilterOrdersRequest,
} from "../../services/admin/orderService";
import { Order } from "../../services/orderService";
import OrderList from "../../components/admin/OrderList";
import OrderDetail from "../../components/admin/OrderDetail";

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filters, setFilters] = useState<FilterOrdersRequest>({
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminOrderService.getAll(filters);
      setOrders(response.orders);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || "فشل تحميل الطلبات");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterOrdersRequest>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleOrderSelect = (order: Order) => {
    setSelectedOrder(order);
  };

  const handleStatusUpdate = async () => {
    await loadOrders();
    if (selectedOrder) {
      const updatedOrder = await adminOrderService.getById(selectedOrder.id);
      setSelectedOrder(updatedOrder);
    }
  };

  const handleQuickStatusUpdate = async (orderId: string, status: string) => {
    try {
      await adminOrderService.updateStatus(orderId, { status });
      // Reload orders list
      await loadOrders();
      // Update selected order if it's the one being updated
      if (selectedOrder && selectedOrder.id === orderId) {
        try {
          const updatedOrder = await adminOrderService.getById(orderId);
          setSelectedOrder(updatedOrder);
        } catch (err) {
          // If getting updated order fails, just reload the list
          console.error("Failed to get updated order:", err);
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "فشل تحديث حالة الطلب";
      alert(errorMessage);
      throw error;
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await adminOrderService.exportCSV(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || "فشل تصدير الطلبات");
    }
  };

  return (
    <div className="relative">
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            إدارة الطلبات
          </h1>
          <button
            onClick={handleExportCSV}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            تصدير CSV
          </button>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <OrderList
          orders={orders}
          loading={loading}
          filters={filters}
          pagination={pagination}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onOrderSelect={handleOrderSelect}
          onStatusUpdate={handleStatusUpdate}
          onQuickStatusUpdate={handleQuickStatusUpdate}
        />
      </div>

      {selectedOrder && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 dark:bg-black/40 z-10"
            onClick={() => setSelectedOrder(null)}
          />
          {/* Modal - Fixed to right side, no left spacing */}
          <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 md:w-[420px] lg:w-[450px] z-20 shadow-2xl">
            <OrderDetail
              order={selectedOrder}
              onClose={() => setSelectedOrder(null)}
              onStatusUpdate={handleStatusUpdate}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Orders;
